import clone from './clone';

let animate = function (elem, style, unit, from, to, time, prop) {
  if (!elem) return;
  let start = new Date().getTime();
  let timer = setInterval(
    function () {
      let step = Math.min(1, (new Date().getTime() - start) / time);
      if (prop) {
        elem[style] = (from + step * (to - from)) + unit;
      } else {
        elem.style[style] = (from + step * (to - from)) + unit;
      }

      if (step === 1) clearInterval(timer);
    },
    16
  );
  elem.style[style] = from + unit;
};

let csrf_token = null;

/** Sets the CSRF token to be sent on ajax calls.
 * Pass the parameter `null` or an empty string to stop sending the header.
 */
let setCSRF = function (token) {
  csrf_token = token;
};

/**
 * Returns a object with the values of the form elements passed on the `keys` parameter,
 * using their "ref" names.
 * `keys` should be either
 * - An Array containing the wanted ref names or
 * - An Object where the keys are the wanted ref names
 */
function getFormValues(component: string, keys: array | object): object {
  let data = {};

  let extractValue = function (key: string, resultKey: string) {
    let obj = component.refs[key];
    if (obj) {
      if (obj.type === 'checkbox') {
        data[resultKey] = obj.checked
      } else if (obj.value) {
        data[resultKey] = obj.value.trim();
      }
    } else {
      data[resultKey] = null;
    }
  };

  if (Array.isArray(keys)) {
    keys.forEach((key) => {
      extractValue(key, key);
    });
  } else if (typeof(keys) === 'object') {
    let myKeys = Object.keys(keys);
    myKeys.forEach((key) => {
      extractValue(key, keys[key]);
    });
  }
  return data;
}

/**
 * This function returns all the form refs in the `keys` parameter as an array.
 * for the bahaviour of the `keys` parameter, refer to the function `getFormValues()` definition.
 * if `filterNull` is true, all null values are filtered out.
 * filterNull defaults to false.
 */
function getFormValuesArray(component: object, keys: array | object, filterNull: boolean): array {
  let data = getFormValues(component, keys);
  let ret = [];

  if (filterNull === undefined) filterNull = false;

  Object.keys(data).forEach((key) => {
    ret.push(data[key]);
  });

  if (filterNull) {
    ret = ret.filter((value) => {
      return (!!value);
    });
  }

  return (ret);
}

function millisToYMD(millis: number): string {
  if (!millis) return '';

  let date = new Date(millis);
  let day = date.getDate();
  let month = date.getMonth() + 1;
  let year = date.getFullYear();
  return '' + year + '-' + (month <= 9 ? '0' + month : month) + '-' + (day <= 9 ? '0' + day : day);
}

function millisToTimeOfDay(millis: number): string {
  if (!millis) return '';

  let date = new Date(millis);
  let hour = date.getHours();
  let minute = date.getMinutes();
  return '' + (hour <= 9 ? '0' + hour : hour) + ':' + (minute <= 9 ? '0' + minute : minute);
}

function millisToDateAndTime(millis: number): string {
  if (!millis) return '';

  return millisToYMD(millis) + ' ' + millisToTimeOfDay(millis);
}

function getTimezoneCode() {
  let date = new Date();
  return date.toLocaleString('en', {timeZoneName: 'short'}).split(' ').pop();
}

function formatCents(cents: number, dropEmptyCents: boolean, dropComma: boolean): string {
  if (cents === null || cents === undefined) return null;

  let returnValue = (cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  if (dropEmptyCents) returnValue = returnValue.replace('.00', '');
  if (dropComma) returnValue = returnValue.replace(/,/g, '');

  return returnValue;
}

const MILLIS_PER_DAY = 1000 * 60 * 60 * 24;

/** Returns the always positive total number of days in between the given dates ignoring time and time zones. */
function dateDiffInDays(date1, date2) {
  // Discard the time and time-zone information.
  let utc1 = Date.UTC(date1.getFullYear(), date1.getMonth(), date1.getDate());
  let utc2 = Date.UTC(date2.getFullYear(), date2.getMonth(), date2.getDate());

  return Math.abs(Math.floor((utc2 - utc1) / MILLIS_PER_DAY));
}

function camelize(str) {
  return str
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/(?:[A-Z]|\b\w)/g, letter => letter.toUpperCase());
}

export default {
  setCSRF: setCSRF,
  clone,
  millisToYMD,
  millisToTimeOfDay,
  millisToDateAndTime,
  getTimezoneCode,

  scrollTo(anchor) {
    let target = document.getElementById(anchor);
    // TODO: Check if target exists
    animate(document.body, 'scrollTop', '', window.pageYOffset, target.offsetTop, 200, true);
  },

  titleCase(str) {
    return (str || '').replace(/[_-]/g, ' ')
      .replace(/\w\S*/g, function (txt) {
        return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
      });
  },

  bindThis(self: object, methods: array) {
    if (!Array.isArray(methods))
      throw new Error('Invalid methods');

    methods.forEach(function (method) {
      try {
        self[method] = self[method].bind(self);
      } catch (e) {
        console.error('error binding method: ' + method);
        console.error(e);
      }
    });
  },

  ajax(params)  {
    // TODO: Maybe use fetch() when available?
    let request = new XMLHttpRequest();

    let url = params.url;

    request.open(params.type, url, true);
    request.withCredentials = true;
    request.setRequestHeader('Content-Type', 'application/json');
    if (csrf_token !== null && csrf_token.length > 0) {
      request.setRequestHeader('X-Csrf-Token', csrf_token);
    }

    request.onreadystatechange = function () {
      if (this.readyState === this.HEADERS_RECEIVED) {
        // Update CSRF value
        let new_csrf = this.getResponseHeader('X-Csrf-Token');
        if (new_csrf !== null) {
          setCSRF(new_csrf);
        }
      }

      if (this.readyState !== 4) return;

      let responseData = null;

      if (this.status !== 204) {
        try {
          responseData = JSON.parse(this.responseText);
        } catch (e) {
          // Some endpoints do return simple strings.
          responseData = this.responseText;
        }
      }

      if ((this.status === 200 || this.status === 204 || this.status === 304) && params.success) {
        params.success(responseData);
      } else if (params.error) {
        params.error(responseData, params.url, this.status);
      }
    };
    request.send(params.data ? JSON.stringify(params.data) : true);
  },

  getFormValues,
  getFormValuesArray,
  formatCents,
  dateDiffInDays,
  camelize,

  dollarToCents(dollars) {
    if (dollars === null || dollars === undefined) return null;

    let dollarString = dollars + '00';
    let dotPosition = dollarString.indexOf('.');
    let truncatedDollar = dotPosition >= 0 ? dollarString.substr(0, dotPosition + 3) : dollarString;
    return parseInt(truncatedDollar.replace('.', ''), 10);
  }
}



/** WEBPACK FOOTER **
 ** ./src/Api/Utils.js
 **/